import React, { useState, useEffect } from 'react';
import { Search, ChevronDown, ChevronUp, Check, RotateCcw, Info, Sparkles } from 'lucide-react';
import { useAuth } from '../contexts/TestAuthContext';
import { api } from '../utils/api';

interface Template {
  id: string;
  name: string;
  description: string;
  icon: string;
  business_types: string[];
  gross_margin_avg: number;
  net_margin_avg: number;
}

interface TemplateDetails {
  id: string;
  name: string;
  description: string;
  icon: string;
  defaults: {
    cogs_pct: number;
    payroll_pct: number;
    rent_monthly: number;
    utilities_monthly: number;
    insurance_monthly: number;
    marketing_pct: number;
    supplies_pct: number;
    other_expenses_pct: number;
  };
  benchmarks: {
    gross_margin_low: number;
    gross_margin_avg: number;
    gross_margin_high: number;
    net_margin_low: number;
    net_margin_avg: number;
    net_margin_high: number;
    labor_cost_pct: number;
    rent_pct: number;
  };
  common_expense_categories: string[];
  tips: string[];
}

interface AppliedTemplateData {
  template_id: string;
  template_name: string;
  template_icon: string;
  applied_to_year: number;
  financial_data: any;
  reconstructed_pl: any;
  defaults_used: any;
  benchmarks: any;
  tips: string[];
  common_categories: string[];
  seasonal_adjustments?: Record<string, number>;
}

interface TemplateSelectorProps {
  businessType?: string;
  onApplyTemplate: (templateData: AppliedTemplateData) => void;
  onReset?: () => void;
  currentRevenue?: number;
  coverageYear?: number;
  appliedTemplateId?: string;
  compact?: boolean;
}

// Helper to generate year range (4 years ending with coverage year)
const getYearRange = (coverageYear: number): number[] => {
  return [coverageYear - 3, coverageYear - 2, coverageYear - 1, coverageYear];
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function TemplateSelector({ 
  businessType, 
  onApplyTemplate,
  onReset,
  currentRevenue = 0,
  coverageYear = new Date().getFullYear(),
  appliedTemplateId,
  compact = false
}: TemplateSelectorProps) {
  const { getToken } = useAuth();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  
  // Multi-year revenue inputs
  const yearRange = getYearRange(coverageYear);
  const [yearlyRevenue, setYearlyRevenue] = useState<Record<number, string>>(() => {
    const initial: Record<number, string> = {};
    yearRange.forEach(year => {
      initial[year] = '';
    });
    return initial;
  });
  
  const [searchQuery, setSearchQuery] = useState('');
  const [isExpanded, setIsExpanded] = useState(!compact);
  const [previewData, setPreviewData] = useState<any>(null);
  
  // Growth rate calculation helper
  const [useGrowthRate, setUseGrowthRate] = useState(false);
  const [baseRevenue, setBaseRevenue] = useState('');
  const [growthRate, setGrowthRate] = useState('5');

  // Fetch templates on mount
  useEffect(() => {
    fetchTemplates();
  }, []);

  // Update yearly revenue when coverage year changes
  useEffect(() => {
    const newYearRange = getYearRange(coverageYear);
    setYearlyRevenue(prev => {
      const updated: Record<number, string> = {};
      newYearRange.forEach(year => {
        updated[year] = prev[year] || '';
      });
      return updated;
    });
  }, [coverageYear]);

  // Apply growth rate to calculate yearly revenues
  const applyGrowthRate = () => {
    if (!baseRevenue || parseFloat(baseRevenue) <= 0) return;
    
    const base = parseFloat(baseRevenue);
    const rate = parseFloat(growthRate) / 100;
    const newRevenues: Record<number, string> = {};
    
    // Work backwards from coverage year
    yearRange.forEach((year, index) => {
      const yearsBack = yearRange.length - 1 - index;
      const calculatedRevenue = base / Math.pow(1 + rate, yearsBack);
      newRevenues[year] = Math.round(calculatedRevenue).toString();
    });
    
    setYearlyRevenue(newRevenues);
    setUseGrowthRate(false);
  };

  // Check if all years have revenue
  const allYearsHaveRevenue = yearRange.every(year => 
    yearlyRevenue[year] && parseFloat(yearlyRevenue[year]) > 0
  );

  // Get total revenue across all years
  const getTotalRevenue = (): number => {
    const revenues = yearRange.map(year => parseFloat(yearlyRevenue[year]) || 0);
    return revenues.reduce((sum, r) => sum + r, 0);
  };

  // Get average revenue per year
  const getAverageRevenue = (): number => {
    return getTotalRevenue() / yearRange.length;
  };

  // NOTE: Removed auto-select behavior - users should explicitly choose a template

  const fetchTemplates = async () => {
    try {
      const token = await getToken();
      const data = await api.get('/api/v1/templates/list', {
        headers: { Authorization: `Bearer ${token}` },
        showErrorToast: false
      });
      setTemplates(data);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const handleSelectTemplate = async (templateId: string) => {
    setLoading(true);
    try {
      const token = await getToken();
      const data = await api.get(`/api/v1/templates/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` },
        showErrorToast: false
      });
      setSelectedTemplate(data);
      setShowDetails(true);
      
      // Auto-preview if all years have revenue
      if (allYearsHaveRevenue) {
        await fetchPreview(templateId, getAverageRevenue());
      }
    } catch (error) {
      console.error('Error fetching template:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPreview = async (templateId: string, avgRevenue: number) => {
    try {
      const token = await getToken();
      const data = await api.get(
        `/api/v1/templates/calculate-estimates?template_id=${templateId}&annual_revenue=${avgRevenue}`,
        { headers: { Authorization: `Bearer ${token}` }, showErrorToast: false }
      );
      setPreviewData(data);
    } catch (error) {
      console.error('Error calculating estimates:', error);
    }
  };

  const handleApply = async () => {
    if (!selectedTemplate || !allYearsHaveRevenue) return;
    
    setApplying(true);
    try {
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };
      
      // Apply template for each year
      try {
        const data: AppliedTemplateData = await api.post(
          `/api/v1/templates/apply-multi-year?template_id=${selectedTemplate.id}`,
          { yearly_revenues: yearlyRevenue, coverage_year: coverageYear },
          { headers }
        );
        onApplyTemplate(data);
      } catch (multiYearError) {
        // Fallback to single-year apply if multi-year endpoint doesn't exist
        const avgRevenue = getAverageRevenue();
        const data: AppliedTemplateData = await api.post(
          `/api/v1/templates/apply?template_id=${selectedTemplate.id}`,
          { annual_revenue: avgRevenue, coverage_year: coverageYear, yearly_revenues: yearlyRevenue },
          { headers }
        );
        onApplyTemplate(data);
      }
      
      setShowDetails(false);
      if (compact) setIsExpanded(false);
    } catch (error) {
      console.error('Error applying template:', error);
      setErrorMessage('Failed to apply template. Please try again.');
    } finally {
      setApplying(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatPercent = (value: number) => `${value.toFixed(1)}%`;

  // Filter templates by search
  const filteredTemplates = templates.filter(t =>
    !searchQuery || 
    t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.business_types.some(bt => bt.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // Compact header for applied template
  if (compact && appliedTemplateId && !isExpanded) {
    const appliedTemplate = templates.find(t => t.id === appliedTemplateId);
    return (
      <div className="bg-gradient-to-r from-indigo-50 to-blue-50 border border-indigo-200 rounded-xl p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-2xl">{appliedTemplate?.icon || '📋'}</span>
            <div>
              <p className="text-sm text-indigo-600 font-medium">Template Applied</p>
              <p className="font-semibold text-gray-900">{appliedTemplate?.name || 'Custom'}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setIsExpanded(true)}
              className="px-3 py-1.5 text-sm text-indigo-600 hover:bg-indigo-100 rounded-lg transition"
            >
              Change
            </button>
            {onReset && (
              <button
                onClick={onReset}
                className="px-3 py-1.5 text-sm text-gray-500 hover:bg-gray-100 rounded-lg transition flex items-center gap-1"
              >
                <RotateCcw className="w-3 h-3" />
                Reset
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200">
      {/* Header */}
      <div 
        className={`bg-gradient-to-r from-indigo-600 to-blue-600 px-6 py-4 text-white ${compact ? 'cursor-pointer' : ''}`}
        onClick={() => compact && setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Sparkles className="w-6 h-6" />
            <div>
              <h3 className="font-bold text-lg">Industry Templates</h3>
              <p className="text-sm text-indigo-100">Pre-fill expense estimates based on industry benchmarks</p>
            </div>
          </div>
          {compact && (
            <button className="p-1 hover:bg-white/20 rounded-lg transition">
              {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
          )}
        </div>
      </div>

      {(!compact || isExpanded) && (
        <div className="p-6">
          {/* Template Selector */}
          {!showDetails && (
            <div className="space-y-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search industries (e.g., restaurant, retail, tech)..."
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Template Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {filteredTemplates.map(template => (
                  <button
                    key={template.id}
                    onClick={() => handleSelectTemplate(template.id)}
                    disabled={loading}
                    className={`p-4 border-2 rounded-xl text-left hover:border-blue-500 hover:bg-blue-50 transition-all group relative ${
                      appliedTemplateId === template.id 
                        ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200' 
                        : 'border-gray-200'
                    } ${loading ? 'opacity-50 cursor-wait' : ''}`}
                  >
                    {appliedTemplateId === template.id && (
                      <div className="absolute -top-2 -right-2 bg-blue-600 text-white rounded-full p-1">
                        <Check className="w-3 h-3" />
                      </div>
                    )}
                    <span className="text-2xl mb-2 block">{template.icon}</span>
                    <h4 className="font-semibold text-gray-900 text-sm leading-tight">{template.name}</h4>
                    <p className="text-xs text-gray-500 mt-1">
                      ~{template.gross_margin_avg}% margin
                    </p>
                  </button>
                ))}
              </div>

              {filteredTemplates.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <p>No templates found matching "{searchQuery}"</p>
                  <button 
                    onClick={() => setSearchQuery('')}
                    className="mt-2 text-blue-600 hover:underline text-sm"
                  >
                    Clear search
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Template Details */}
          {showDetails && selectedTemplate && (
            <div className="space-y-6">
              {/* Template Header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-4xl">{selectedTemplate.icon}</span>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">{selectedTemplate.name}</h3>
                    <p className="text-sm text-gray-500">{selectedTemplate.description}</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowDetails(false);
                    setSelectedTemplate(null);
                    setPreviewData(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 flex items-center gap-1"
                >
                  ← Change template
                </button>
              </div>

              {/* Multi-Year Revenue Input */}
              <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-lg p-4 border border-gray-200">
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-gray-700">
                    Enter Annual Revenue by Year
                  </label>
                  <button
                    onClick={() => setUseGrowthRate(!useGrowthRate)}
                    className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                  >
                    {useGrowthRate ? '← Enter manually' : '⚡ Quick fill with growth rate'}
                  </button>
                </div>
                
                <p className="text-xs text-gray-500 mb-4">
                  💡 The BI calculator needs multiple years of historical data to establish benchmarks. Enter revenue for each year.
                </p>

                {useGrowthRate ? (
                  <div className="bg-white rounded-lg p-4 border border-blue-200 space-y-4">
                    <p className="text-sm text-blue-800 font-medium">Quick Fill Calculator</p>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">
                          Most Recent Year Revenue ({coverageYear})
                        </label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                          <input
                            type="number"
                            value={baseRevenue}
                            onChange={(e) => setBaseRevenue(e.target.value)}
                            placeholder="500,000"
                            className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Annual Growth Rate (%)</label>
                        <div className="relative">
                          <input
                            type="number"
                            value={growthRate}
                            onChange={(e) => setGrowthRate(e.target.value)}
                            placeholder="5"
                            className="w-full px-4 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">%</span>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={applyGrowthRate}
                      disabled={!baseRevenue || parseFloat(baseRevenue) <= 0}
                      className="w-full py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 transition"
                    >
                      Calculate & Fill All Years
                    </button>
                    <p className="text-xs text-gray-500">
                      This will calculate historical revenues assuming {growthRate}% annual growth leading to your current revenue.
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {yearRange.map((year, index) => (
                      <div key={year}>
                        <label className="block text-xs text-gray-600 mb-1 font-medium">
                          {year}
                          {index === yearRange.length - 1 && (
                            <span className="text-blue-600 ml-1">(Coverage Year)</span>
                          )}
                        </label>
                        <div className="relative">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                          <input
                            type="number"
                            value={yearlyRevenue[year]}
                            onChange={(e) => setYearlyRevenue({...yearlyRevenue, [year]: e.target.value})}
                            placeholder="500,000"
                            className={`w-full pl-6 pr-2 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${
                              yearlyRevenue[year] && parseFloat(yearlyRevenue[year]) > 0
                                ? 'border-green-300 bg-green-50'
                                : 'border-gray-300'
                            }`}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {!useGrowthRate && (
                  <div className="mt-4">
                    {/* Show entered values summary */}
                    {allYearsHaveRevenue && (
                      <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-sm font-medium text-blue-800 mb-2">Your Entered Revenue:</p>
                        <div className="flex flex-wrap gap-2">
                          {yearRange.map(year => (
                            <span key={year} className="text-xs bg-white px-2 py-1 rounded border border-blue-200">
                              {year}: ${parseInt(yearlyRevenue[year]).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                          ))}
                          <span className="text-xs bg-blue-600 text-white px-2 py-1 rounded font-medium">
                            Total: ${getTotalRevenue().toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        </div>
                      </div>
                    )}
                    <div className="flex justify-end">
                      <button
                        onClick={() => selectedTemplate && fetchPreview(selectedTemplate.id, getAverageRevenue())}
                        disabled={!allYearsHaveRevenue}
                        className="px-6 py-2.5 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
                      >
                        {allYearsHaveRevenue ? 'Preview Monthly Breakdown' : `Enter all ${yearRange.length} years`}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Preview Results */}
              {previewData && (
                <div className="space-y-4">
                  {/* What Will Be Generated */}
                  <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg p-4 text-white">
                    <h4 className="font-bold text-lg mb-2">📅 {yearRange.length * 12} Months of Data Will Be Generated</h4>
                    <p className="text-blue-100 text-sm">
                      This template will create monthly financial data for {yearRange[0]}-{yearRange[yearRange.length-1]} 
                      ({yearRange.length} years × 12 months = {yearRange.length * 12} data points per category)
                    </p>
                  </div>

                  {/* Monthly Breakdown by Year */}
                  <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                    <div className="bg-gray-50 px-4 py-3 border-b">
                      <h4 className="font-semibold text-gray-900">📊 Monthly Revenue Breakdown (to be generated)</h4>
                    </div>
                    <div className="p-4">
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b">
                              <th className="text-left py-2 px-2 font-medium text-gray-600">Year</th>
                              <th className="text-right py-2 px-2 font-medium text-gray-600">Annual Revenue</th>
                              <th className="text-right py-2 px-2 font-medium text-gray-600">Monthly Avg</th>
                              <th className="text-right py-2 px-2 font-medium text-gray-600">Monthly COGS</th>
                              <th className="text-right py-2 px-2 font-medium text-gray-600">Monthly Payroll</th>
                              <th className="text-right py-2 px-2 font-medium text-gray-600">Monthly Expenses</th>
                            </tr>
                          </thead>
                          <tbody>
                            {yearRange.map(year => {
                              const yearRev = parseFloat(yearlyRevenue[year] || '0');
                              const monthlyRev = yearRev / 12;
                              const monthlyCOGS = monthlyRev * (previewData.estimates.cogs / previewData.annual_revenue);
                              const monthlyPayroll = monthlyRev * (previewData.estimates.payroll / previewData.annual_revenue);
                              const monthlyExp = (previewData.estimates.rent + previewData.estimates.utilities + 
                                                  previewData.estimates.insurance + previewData.estimates.marketing +
                                                  previewData.estimates.supplies + previewData.estimates.other) / 12;
                              return (
                                <tr key={year} className="border-b last:border-b-0 hover:bg-gray-50">
                                  <td className="py-2 px-2 font-medium">{year}</td>
                                  <td className="py-2 px-2 text-right text-green-600 font-medium">
                                    ${yearRev.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </td>
                                  <td className="py-2 px-2 text-right">
                                    ${Math.round(monthlyRev).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </td>
                                  <td className="py-2 px-2 text-right text-amber-600">
                                    ${Math.round(monthlyCOGS).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </td>
                                  <td className="py-2 px-2 text-right text-blue-600">
                                    ${Math.round(monthlyPayroll).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </td>
                                  <td className="py-2 px-2 text-right text-gray-600">
                                    ${Math.round(monthlyExp).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                          <tfoot className="bg-gray-50 font-semibold">
                            <tr>
                              <td className="py-2 px-2">Total ({yearRange.length} years)</td>
                              <td className="py-2 px-2 text-right text-green-700">
                                ${getTotalRevenue().toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </td>
                              <td colSpan={4} className="py-2 px-2 text-right text-gray-500 text-xs">
                                {yearRange.length * 12} monthly records will be created
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>
                  </div>

                  {/* Sample Monthly View for ALL Years */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-3">
                      📆 Monthly Data Preview (All {yearRange.length} Years)
                    </h4>
                    <p className="text-xs text-gray-500 mb-4">
                      Your annual revenue inputs will be converted to monthly data. Here's what each year will look like:
                    </p>
                    
                    {/* Show monthly preview for each year */}
                    <div className="space-y-4">
                      {yearRange.map(year => {
                        const yearRev = parseFloat(yearlyRevenue[year] || '0');
                        if (yearRev <= 0) return null;
                        const monthlyRev = yearRev / 12;
                        
                        return (
                          <div key={year} className="bg-white rounded-lg p-3 border border-gray-200">
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-semibold text-gray-800">{year}</span>
                              <span className="text-xs text-gray-500">
                                Annual: ${yearRev.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} → Monthly avg: ${Math.round(monthlyRev).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </span>
                            </div>
                            <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-12 gap-1">
                              {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((month, idx) => {
                                // Apply seasonal adjustment if template has it
                                const seasonalAdj = previewData.seasonal_adjustments ? 
                                  (idx < 3 ? previewData.seasonal_adjustments.Q1 :
                                   idx < 6 ? previewData.seasonal_adjustments.Q2 :
                                   idx < 9 ? previewData.seasonal_adjustments.Q3 :
                                   previewData.seasonal_adjustments.Q4) || 1 : 1;
                                const adjustedRev = monthlyRev * seasonalAdj;
                                return (
                                  <div key={month} className="bg-gray-50 rounded p-1.5 border border-gray-100 text-center">
                                    <p className="text-[10px] text-gray-400 font-medium">{month}</p>
                                    <p className="text-[10px] font-bold text-green-600">${Math.round(adjustedRev / 1000)}K</p>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    
                    {previewData.seasonal_adjustments && (
                      <p className="text-xs text-blue-600 mt-3">
                        ✨ Seasonal adjustments applied: Q1 ({previewData.seasonal_adjustments.Q1}x), 
                        Q2 ({previewData.seasonal_adjustments.Q2}x), 
                        Q3 ({previewData.seasonal_adjustments.Q3}x), 
                        Q4 ({previewData.seasonal_adjustments.Q4}x)
                      </p>
                    )}
                  </div>

                  {/* Template Ratios Used */}
                  <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                    <h4 className="font-semibold text-amber-900 mb-3 flex items-center gap-2">
                      <Info className="w-4 h-4" />
                      Industry Ratios Applied (from {selectedTemplate?.name} template)
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                      <div className="bg-white p-2 rounded border border-amber-200">
                        <span className="text-gray-600">COGS:</span>
                        <span className="font-bold ml-2">{((previewData.estimates.cogs / previewData.annual_revenue) * 100).toFixed(1)}%</span>
                      </div>
                      <div className="bg-white p-2 rounded border border-amber-200">
                        <span className="text-gray-600">Payroll:</span>
                        <span className="font-bold ml-2">{((previewData.estimates.payroll / previewData.annual_revenue) * 100).toFixed(1)}%</span>
                      </div>
                      <div className="bg-white p-2 rounded border border-amber-200">
                        <span className="text-gray-600">Gross Margin:</span>
                        <span className="font-bold ml-2 text-green-600">{previewData.calculated_margins.gross_margin_pct}%</span>
                      </div>
                      <div className="bg-white p-2 rounded border border-amber-200">
                        <span className="text-gray-600">Net Margin:</span>
                        <span className="font-bold ml-2 text-blue-600">{previewData.calculated_margins.net_margin_pct}%</span>
                      </div>
                    </div>
                  </div>

                  {/* Industry Benchmarks */}
                  <div className="bg-indigo-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-3">📊 vs Industry Benchmarks</h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">Gross Margin:</span>
                        <span className={`font-bold ${
                          previewData.calculated_margins.gross_margin_pct >= previewData.benchmarks.industry_gross_margin 
                            ? 'text-green-600' : 'text-amber-600'
                        }`}>
                          {formatPercent(previewData.calculated_margins.gross_margin_pct)}
                          <span className="text-gray-400 ml-1 font-normal">(avg: {formatPercent(previewData.benchmarks.industry_gross_margin)})</span>
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">Net Margin:</span>
                        <span className={`font-bold ${
                          previewData.calculated_margins.net_margin_pct >= previewData.benchmarks.industry_net_margin 
                            ? 'text-green-600' : 'text-amber-600'
                        }`}>
                          {formatPercent(previewData.calculated_margins.net_margin_pct)}
                          <span className="text-gray-400 ml-1 font-normal">(avg: {formatPercent(previewData.benchmarks.industry_net_margin)})</span>
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Tips */}
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <h4 className="font-semibold text-yellow-800 mb-2 flex items-center gap-2">
                      💡 Industry Tips
                    </h4>
                    <ul className="space-y-1 text-sm text-yellow-700">
                      {selectedTemplate.tips.slice(0, 3).map((tip, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <span className="mt-1">•</span>
                          <span>{tip}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  onClick={() => {
                    setShowDetails(false);
                    setSelectedTemplate(null);
                    setPreviewData(null);
                  }}
                  className="px-6 py-3 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleApply}
                  disabled={!previewData || !allYearsHaveRevenue || applying}
                  className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition"
                >
                  {applying ? (
                    <>
                      <span className="animate-spin">⏳</span>
                      Applying...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" />
                      Apply Template ({yearRange.length} Years)
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
